import { apiRequest } from '../../../shared/services/apiClient';
import {
  CreateRoleRequest,
  EmployeeRoleAssignment,
  EmployeeRoleAssignmentListQuery,
  Permission,
  Role,
  RoleListQuery,
  UpdateEmployeeRolesRequest,
  UpdateRoleRequest,
} from '../types/Role';

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const normalizePermission = (value: any): Permission => ({
  id: String(value?.id ?? ''),
  code: String(value?.code ?? ''),
  name: String(value?.name ?? ''),
  description: value?.description ? String(value.description) : undefined,
  category: value?.category ? String(value.category) : undefined,
  isActive: Boolean(value?.isActive),
});

const normalizeRole = (value: any): Role => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  description: value?.description ? String(value.description) : undefined,
  isSystemRole: Boolean(value?.isSystemRole),
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
  permissions: pickItems(value?.permissions).map(normalizePermission),
  assignedEmployeeCount: Number(value?.assignedEmployeeCount ?? 0),
});

const normalizeAssignment = (value: any): EmployeeRoleAssignment => ({
  employeeId: String(value?.employeeId ?? ''),
  employeeName: String(value?.employeeName ?? ''),
  employeeEmail: String(value?.employeeEmail ?? ''),
  employeeTitle: value?.employeeTitle ? String(value.employeeTitle) : undefined,
  employeeIsActive: Boolean(value?.employeeIsActive),
  roleIds: Array.isArray(value?.roleIds) ? value.roleIds.map((roleId: any) => String(roleId)) : [],
});

const toQueryString = (query?: Record<string, unknown>): string => {
  if (!query) return '';

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, rawValue]) => {
    if (rawValue === undefined || rawValue === null) return;
    if (typeof rawValue === 'string' && !rawValue.trim()) return;
    params.set(key, String(rawValue));
  });

  const search = params.toString();
  return search ? `?${search}` : '';
};

export const roleService = {
  async listPermissions(): Promise<Permission[]> {
    const payload = await apiRequest<any>('/api/v1/roles/permissions');
    return pickItems(payload).map(normalizePermission);
  },

  async listRoles(query?: RoleListQuery): Promise<Role[]> {
    const payload = await apiRequest<any>(`/api/v1/roles${toQueryString({
      Q: query?.q,
      IsActive: query?.isActive,
      SortBy: query?.sortBy,
      SortDir: query?.sortDir,
      Page: query?.page,
      PageSize: query?.pageSize,
    })}`);

    return pickItems(payload).map(normalizeRole);
  },

  async createRole(request: CreateRoleRequest): Promise<Role | null> {
    const payload = await apiRequest<any>('/api/v1/roles', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeRole(item) : null;
  },

  async updateRole(roleId: string, request: UpdateRoleRequest): Promise<Role | null> {
    const payload = await apiRequest<any>(`/api/v1/roles/${roleId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeRole(item) : null;
  },

  async listEmployeeRoleAssignments(query?: EmployeeRoleAssignmentListQuery): Promise<EmployeeRoleAssignment[]> {
    const payload = await apiRequest<any>(`/api/v1/roles/employees${toQueryString({
      Q: query?.q,
      IsEmployeeActive: query?.isEmployeeActive,
      Page: query?.page,
      PageSize: query?.pageSize,
    })}`);

    return pickItems(payload).map(normalizeAssignment);
  },

  async updateEmployeeRoles(employeeId: string, request: UpdateEmployeeRolesRequest): Promise<EmployeeRoleAssignment | null> {
    const payload = await apiRequest<any>(`/api/v1/roles/employees/${employeeId}/roles`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeAssignment(item) : null;
  },
};

export const listPermissions = roleService.listPermissions;
export const listRoles = roleService.listRoles;
export const createRole = roleService.createRole;
export const updateRole = roleService.updateRole;
export const listEmployeeRoleAssignments = roleService.listEmployeeRoleAssignments;
export const updateEmployeeRoles = roleService.updateEmployeeRoles;
